/**
 * Logout-on-401: any 401 from an internal API call logs the user out
 * (design §1.2–§1.4). ``query-client.ts`` wires ``handleQueryError`` /
 * ``handleMutationError`` into the ``QueryCache``/``MutationCache``
 * ``onError`` callbacks, the one place that sees every failed call.
 *
 * - **Query 401** → hard navigation to ``/signin`` (a background read has
 *   nothing to protect; the document load wipes in-memory authed state).
 * - **Mutation 401** → a soft "signed out" prompt that leaves the form
 *   mounted so the user keeps their input (PM decision §6.1).
 *
 * Only a user who had a session gets the ``session_expired`` notice; a
 * cold-load 401 is a plain gate to sign-in. A same-tick fan-out of 401s
 * collapses into one reaction, never a stack (§6.5).
 */

import { ApiError } from "@/lib/api/client"
import { isPublicRoute, sanitizeNextPath } from "@/lib/auth/public-routes"
import { showSessionExpiredToast } from "@/components/session-expired-toast"

/**
 * Build the ``/signin`` target for an unauthorized call, or ``null`` on a
 * public route (where a 401 is expected and must not bounce the visitor).
 * ``next`` is omitted when it fails ``sanitizeNextPath``;
 * ``auth_error=session_expired`` is added only for a mid-session expiry
 * (§1.3.1).
 */
export function signinRedirectForUnauthorized(
  pathname: string,
  search: string,
  { expired }: { expired: boolean },
): string | null {
  if (isPublicRoute(pathname)) {
    return null
  }
  const normalizedSearch = search.startsWith("?") ? search.slice(1) : search
  const currentPath = normalizedSearch
    ? `${pathname}?${normalizedSearch}`
    : pathname
  const safeNext = sanitizeNextPath(currentPath)

  const parts: string[] = []
  if (safeNext) {
    parts.push(`next=${encodeURIComponent(safeNext)}`)
  }
  if (expired) {
    parts.push("auth_error=session_expired")
  }
  return parts.length > 0 ? `/signin?${parts.join("&")}` : "/signin"
}

// Effects are swappable because jsdom's ``window.location`` is immutable;
// tests inject fakes via ``setSessionExpiryEffectsForTests``.

export interface SessionExpiryEffects {
  /** Query path: hard, state-wiping navigation to the sign-in target. */
  navigate: (target: string) => void
  /** Mutation path: non-blocking re-auth prompt with a Sign in action. */
  promptReauth: (target: string) => void
}

const defaultEffects: SessionExpiryEffects = {
  navigate: (target) => {
    window.location.replace(target)
  },
  promptReauth: (target) => {
    showSessionExpiredToast(target)
  },
}

/**
 * ``meta`` key opting a mutation out of the 401 prompt. Sign out sets it:
 * its logout POST may 401 on an already-dead session, and a "signed out"
 * prompt over a deliberate logout would mislead.
 */
const SUPPRESS_META_KEY = "suppressAuthExpiryHandler"

/** Ready-made ``meta`` value that exempts a mutation from the 401 prompt. */
export const suppressSessionExpiryMeta = { [SUPPRESS_META_KEY]: true } as const

/**
 * Reaction fired this tick. A query redirect supersedes a mutation prompt
 * regardless of arrival order; same-kind 401s dedupe (§6.5). Reset on the
 * next macrotask so a later, independent 401 can still react.
 */
type Reaction = "none" | "redirecting" | "prompted"

let effects: SessionExpiryEffects = defaultEffects
let sessionWasEstablished = false
let reaction: Reaction = "none"

/**
 * Record that a real session existed (on the first ``/api/auth/me/``
 * success), so later 401s read as a mid-session expiry. Only a full
 * document load clears it; a future soft re-login flow must reset it
 * explicitly to avoid a stale ``session_expired`` notice.
 */
export function markSessionEstablished(): void {
  sessionWasEstablished = true
}

/** The request was refused because the session is missing or expired. */
export function isUnauthorized(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401
}

function currentRedirectTarget(): string | null {
  return signinRedirectForUnauthorized(
    window.location.pathname,
    window.location.search,
    { expired: sessionWasEstablished },
  )
}

function scheduleReactionReset(): void {
  setTimeout(() => {
    reaction = "none"
  }, 0)
}

/** ``QueryCache.onError``: a query 401 hard-redirects to sign-in, superseding any prompt. */
export function handleQueryError(error: unknown): void {
  if (!isUnauthorized(error)) {
    return
  }
  if (reaction === "redirecting") {
    return
  }
  const target = currentRedirectTarget()
  if (target === null) {
    return
  }
  reaction = "redirecting"
  scheduleReactionReset()
  effects.navigate(target)
}

/**
 * ``MutationCache.onError``: a mutation 401 raises the soft re-auth prompt,
 * keeping the user's input; skipped when any reaction is already in flight.
 */
export function handleMutationError(
  error: unknown,
  meta?: Record<string, unknown>,
): void {
  if (meta?.[SUPPRESS_META_KEY] === true) {
    return
  }
  if (!isUnauthorized(error)) {
    return
  }
  if (reaction !== "none") {
    return
  }
  const target = currentRedirectTarget()
  if (target === null) {
    return
  }
  reaction = "prompted"
  scheduleReactionReset()
  effects.promptReauth(target)
}

/** Test seam: override the effects; ``null`` restores the defaults. */
export function setSessionExpiryEffectsForTests(
  next: Partial<SessionExpiryEffects> | null,
): void {
  effects = next ? { ...defaultEffects, ...next } : defaultEffects
}

/** Test seam: clear the reaction latch and the session-established flag. */
export function resetSessionExpiryStateForTests(): void {
  sessionWasEstablished = false
  reaction = "none"
}
